import { useState, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { saRadiosData, type SARadio, type SARadioMasterStatus } from '@/mocks/superAdminRadios';
import RadioFilters from './components/RadioFilters';
import RadiosTable from './components/RadiosTable';
import RadioDetailModal from './components/RadioDetailModal';
import ChangePlanModal from './components/ChangePlanModal';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function SuperAdminRadios() {
  /* ── Data ── */
  const [radios, setRadios] = useState<SARadio[]>(saRadiosData);
  const [activeFilter, setActiveFilter] = useState('todas');
  const [searchTerm, setSearchTerm] = useState('');

  /* ── Modals ── */
  const [viewRadio, setViewRadio] = useState<SARadio | null>(null);
  const [planRadio, setPlanRadio] = useState<SARadio | null>(null);

  /* ── UI states ── */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [inlineMsg, setInlineMsg] = useState<string | null>(null);

  const simulatedRef = useRef(false);

  const showInline = useCallback((message: string) => {
    setInlineMsg(message);
    setTimeout(() => setInlineMsg(null), 3500);
  }, []);

  /* ── Simulate initial loading ── */
  if (!simulatedRef.current) {
    simulatedRef.current = true;
    setTimeout(async () => {
      setLoading(true);
      await delay(900);
      setLoading(false);
    }, 0);
  }

  /* ── Filter counts ── */
  const counts = useMemo(() => {
    const data = saRadiosData;
    return {
      todas: data.length,
      activas: data.filter((r) => r.status === 'activa').length,
      solicitudes: data.filter((r) => r.status === 'solicitud_recibida').length,
      en_revision: data.filter((r) => r.status === 'en_revision').length,
      pendiente_contrato: data.filter((r) => r.status === 'pendiente_contrato' || r.status === 'pendiente_firma').length,
      pendiente_pago: data.filter((r) => r.status === 'pendiente_pago').length,
      suspendidas: data.filter((r) => r.status === 'suspendida').length,
      canceladas: data.filter((r) => r.status === 'cancelada' || r.status === 'archivada' || r.status === 'vencida').length,
      'stream-activo': data.filter((r) => r.streamStatusV2 === 'online').length,
      'stream-caido': data.filter((r) => r.streamStatusV2 === 'offline' || r.streamStatusV2 === 'error' || r.streamStatusV2 === 'sin_respuesta').length,
      'stream-inestable': data.filter((r) => r.streamStatusV2 === 'inestable').length,
      free: data.filter((r) => r.plan === 'free').length,
      pro: data.filter((r) => r.plan === 'pro').length,
      premium: data.filter((r) => r.plan === 'premium').length,
    };
  }, []);

  /* ── Filter + search ── */
  const filteredRadios = useMemo(() => {
    let result = radios;
    if (activeFilter === 'activas') result = result.filter((r) => r.status === 'activa');
    if (activeFilter === 'solicitudes') result = result.filter((r) => r.status === 'solicitud_recibida');
    if (activeFilter === 'en_revision') result = result.filter((r) => r.status === 'en_revision');
    if (activeFilter === 'pendiente_contrato') result = result.filter((r) => r.status === 'pendiente_contrato' || r.status === 'pendiente_firma');
    if (activeFilter === 'pendiente_pago') result = result.filter((r) => r.status === 'pendiente_pago');
    if (activeFilter === 'suspendidas') result = result.filter((r) => r.status === 'suspendida');
    if (activeFilter === 'canceladas') result = result.filter((r) => r.status === 'cancelada' || r.status === 'archivada' || r.status === 'vencida');
    if (activeFilter === 'stream-activo') result = result.filter((r) => r.streamStatusV2 === 'online');
    if (activeFilter === 'stream-caido') result = result.filter((r) => r.streamStatusV2 === 'offline' || r.streamStatusV2 === 'error' || r.streamStatusV2 === 'sin_respuesta');
    if (activeFilter === 'stream-inestable') result = result.filter((r) => r.streamStatusV2 === 'inestable');
    if (activeFilter === 'free') result = result.filter((r) => r.plan === 'free');
    if (activeFilter === 'pro') result = result.filter((r) => r.plan === 'pro');
    if (activeFilter === 'premium') result = result.filter((r) => r.plan === 'premium');
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.frequency.toLowerCase().includes(q) ||
          r.city.toLowerCase().includes(q) ||
          r.country.toLowerCase().includes(q) ||
          r.ownerName.toLowerCase().includes(q),
      );
    }
    return result;
  }, [radios, activeFilter, searchTerm]);

  const hasFilters = activeFilter !== 'todas' || searchTerm.trim() !== '';

  const handleRetry = useCallback(async () => {
    setError(undefined);
    setLoading(true);
    await delay(1000);
    setRadios(saRadiosData);
    setLoading(false);
    showInline('Radios cargadas correctamente');
  }, []);

  /* ══════════════════════════════════════
     ACTIONS
  ══════════════════════════════════════ */

  const handleSuspend = useCallback(
    async (radio: SARadio) => {
      const result = await Swal.fire({
        title: 'Suspender radio',
        html: `
          <div style="text-align:left;font-size:13px">
            <p style="margin-bottom:10px">Vas a suspender <strong>${radio.name}</strong></p>
            <label style="display:block;margin-bottom:4px;color:#6b7280;font-size:11px;font-weight:500">Motivo de suspensión</label>
            <input id="swal-suspend-reason" type="text" class="swal2-input" placeholder="Ej: Violación de términos..." style="width:100%;box-sizing:border-box">
          </div>
        `,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'Suspender radio', cancelButtonText: 'Cancelar',
        confirmButtonColor: '#ef4444', cancelButtonColor: '#d1d5db',
        customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', htmlContainer: 'text-sm' },
        buttonsStyling: false,
        preConfirm: () => {
          const input = document.getElementById('swal-suspend-reason') as HTMLInputElement;
          if (!input?.value.trim()) { Swal.showValidationMessage('Escribí el motivo'); return false; }
          return input.value.trim();
        },
      });
      if (result.isConfirmed) {
        const motivo = result.value;
        const now = new Date().toISOString();
        setRadios((prev) =>
          prev.map((r) =>
            r.id === radio.id
              ? { ...r, status: 'suspendida' as SARadioMasterStatus, suspendReason: motivo, statusHistory: [...r.statusHistory, { id: Math.max(0, ...r.statusHistory.map((h) => h.id)) + 1, fromStatus: r.status, toStatus: 'suspendida' as SARadioMasterStatus, changedBy: 'María Gómez (SA)', changedAt: now, reason: motivo }] }
              : r,
          ),
        );
      }
    },
    [],
  );

  const handleReactivate = useCallback(
    async (radio: SARadio) => {
      const result = await Swal.fire({
        title: 'Reactivar radio',
        html: `<div style="text-align:left;font-size:13px"><p style="margin-bottom:10px">Vas a reactivar <strong>${radio.name}</strong></p><p style="color:#6b7280">La radio volverá al estado <strong style="color:#10b981">Activa</strong>.</p></div>`,
        icon: 'question', showCancelButton: true, confirmButtonText: 'Sí, reactivar', cancelButtonText: 'Cancelar',
        confirmButtonColor: '#10b981', cancelButtonColor: '#d1d5db',
        customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', htmlContainer: 'text-sm' },
        buttonsStyling: false,
      });
      if (result.isConfirmed) {
        const now = new Date().toISOString();
        setRadios((prev) =>
          prev.map((r) =>
            r.id === radio.id
              ? { ...r, status: 'activa' as SARadioMasterStatus, suspendReason: undefined, rejectedReason: undefined, statusHistory: [...r.statusHistory, { id: Math.max(0, ...r.statusHistory.map((h) => h.id)) + 1, fromStatus: r.status, toStatus: 'activa' as SARadioMasterStatus, changedBy: 'María Gómez (SA)', changedAt: now, reason: 'Reactivación manual por Super Admin.' }] }
              : r,
          ),
        );
      }
    },
    [],
  );

  const handleChangePlan = useCallback(
    (radio: SARadio, newPlan: SARadio['plan']) => {
      const storageMap = { free: 1, pro: 50, premium: 500 };
      setRadios((prev) => prev.map((r) => (r.id === radio.id ? { ...r, plan: newPlan, storageTotalGB: storageMap[newPlan], storageUsedGB: Math.min(r.storageUsedGB, storageMap[newPlan]) } : r)));
    },
    [],
  );

  /* ══════════════════════════════════════
     RENDER
  ══════════════════════════════════════ */

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Gestión de Radios</span>
            </div>

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Gestión de Radios</h2>
                <p className="text-foreground-400 text-xs mt-0.5">{filteredRadios.length} de {radios.length} radios</p>
              </div>
              <div className="relative w-full sm:w-64">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                <input
                  type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar radio, ciudad, país..."
                  className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors">
                    <i className="ri-close-line text-foreground-500 text-[10px]"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Inline message */}
            {inlineMsg && (
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-[10px] font-medium w-fit">
                <i className="ri-check-line text-emerald-500 text-xs"></i>
                {inlineMsg}
              </div>
            )}

            {/* Filters */}
            <RadioFilters activeFilter={activeFilter} onFilterChange={setActiveFilter} counts={counts} />

            {/* Table */}
            <RadiosTable
              radios={filteredRadios}
              onView={setViewRadio}
              loading={loading}
              error={error}
              onRetry={handleRetry}
              hasFilters={hasFilters}
            />
          </div>
        </main>
      </div>

      {/* Modals */}
      <RadioDetailModal
        radio={viewRadio}
        onClose={() => setViewRadio(null)}
        onSuspend={handleSuspend}
        onReactivate={handleReactivate}
        onChangePlan={setPlanRadio}
      />

      <ChangePlanModal radio={planRadio} onClose={() => setPlanRadio(null)} onConfirm={handleChangePlan} />

      {/* Mobile bottom hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Gestión de Radios</p>
      </div>
    </div>
  );
}